import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { CartState } from '@core/models/cart.state';
import { FulfilmentMethod, ListingStatus } from '@core/models/listing.enum';
import { ListingDetailState } from '@core/models/listing.state';
import { OrderGroupStatus, ReservationStatus } from '@core/models/order.enum';
import { CheckoutSessionState, OrderGroupState, OrderState, ShippingRateState } from '@core/models/order.state';
import { PaginatedResult } from '@core/models/paginated.response';
import { StoreState } from '@core/models/store.state';
import { MockDataService } from '@core/services/mock-data.service';

import { seedListings } from '../listing/listing.seed';
import { mockError, mockResponse } from '../mock-response';
import { seedPlatformConfig } from '../platform-config/platform-config.seed';
import { seedStores } from '../store/store.seed';
import { seedTenants } from '../tenant/tenant.seed';
import { SHIPPING_ETA_FALLBACK, buildOrderTotals, mintPickupTicket, nextOrderRef, roundMoney } from './order-build';
import { OrderRepository, PlaceOrderRequest } from './order.repository';
import { seedOrders } from './order.seed';

/** Checkout hold window (HLD §8.1 D-04), owned by platform config (ADM-02). */
const RESERVATION_WINDOW_MS = seedPlatformConfig().reservationWindowMs;

/** Server-private reservation row - which session holds which listing. */
interface ReservationEntry {
  id: number;
  sessionId: number;
  listingId: number;
}

/**
 * In-memory checkout + order engine (HLD §8, §11): checkout-initiated
 * reservations with conflicts, order placement with per-store groups and
 * pickup tickets, and the shopper's order history.
 */
@Injectable()
export class OrderMockRepository implements OrderRepository {
  private readonly mockData = inject(MockDataService);
  private readonly sessions = this.mockData.collection<CheckoutSessionState>('checkout-sessions');
  private readonly reservations = this.mockData.collection<ReservationEntry>('reservations');
  private readonly orders = this.mockData.collection<OrderState>('orders', seedOrders);
  private readonly carts = this.mockData.collection<CartState>('carts');
  private readonly listings = this.mockData.collection<ListingDetailState>('listings', seedListings);
  private readonly stores = this.mockData.collection<StoreState>('stores', seedStores);

  /**
   * Mock rate shopping (SHP-01). A real carrier call prices weight, dims and
   * destination; here the three service tiers scale off the store's own base
   * rate so the ordering and the free-shipping rule behave realistically. The
   * threshold applies to the cheapest service only, as carriers price it.
   */
  getShippingRates(storeId: number, itemsSubtotal: number): Observable<ShippingRateState[]> {
    return mockResponse(this.computeRates(storeId, itemsSubtotal));
  }

  /** Synchronous core - placement re-prices with it, so it cannot await. */
  private computeRates(storeId: number, itemsSubtotal: number): ShippingRateState[] {
    const store = this.stores.get(storeId);
    const tenant = store ? seedTenants().find(t => t.id === store.tenantId) : undefined;
    const base = this.baseRateFor(storeId);
    const threshold = tenant?.policy.freeShippingThreshold ?? null;
    const qualifiesFree = threshold !== null && itemsSubtotal >= threshold;

    return [
      {
        code: 'usps-ground',
        carrier: 'USPS',
        service: 'Ground Advantage',
        amount: qualifiesFree ? 0 : base,
        etaLabel: 'Arrives in 4-7 business days',
        isFree: qualifiesFree
      },
      {
        code: 'ups-2day',
        carrier: 'UPS',
        service: '2nd Day Air',
        amount: roundMoney(base * 1.8 + 3),
        etaLabel: 'Arrives in 2 business days',
        isFree: false
      },
      {
        code: 'fedex-overnight',
        carrier: 'FedEx',
        service: 'Standard Overnight',
        amount: roundMoney(base * 3.2 + 8),
        etaLabel: 'Arrives next business day',
        isFree: false
      }
    ];
  }

  /** Re-price a chosen service at placement so the amount cannot be spoofed. */
  private rateByCode(storeId: number, itemsSubtotal: number, code: string): ShippingRateState | null {
    return this.computeRates(storeId, itemsSubtotal).find(rate => rate.code === code) ?? null;
  }

  /** Cheapest live shipping price among the store's listings, floored at $6.95. */
  private baseRateFor(storeId: number): number {
    const prices = this.listings
      .list()
      .filter(listing => listing.storeId === storeId && listing.shippingPrice !== null)
      .map(listing => listing.shippingPrice as number);
    return prices.length === 0 ? 6.95 : Math.max(6.95, Math.min(...prices));
  }

  startCheckout(ownerKey: string): Observable<CheckoutSessionState> {
    this.expireLapsedSessions();
    const cart = this.carts.list().find(c => c.ownerKey === ownerKey);
    if (!cart || cart.lines.length === 0) {
      return mockError('Your cart is empty.');
    }

    const reservedElsewhere = new Set(this.reservations.list().map(r => r.listingId));
    const conflictListingIds: number[] = [];

    const session: CheckoutSessionState = this.sessions.upsert({
      id: this.sessions.allocateId(),
      ownerKey,
      reservationExpiresAt: new Date(Date.now() + RESERVATION_WINDOW_MS),
      status: ReservationStatus.Active,
      conflictListingIds
    });

    for (const line of cart.lines) {
      const listing = this.listings.get(line.listingId);
      const gone = !listing || listing.status !== ListingStatus.Live;
      if (gone || reservedElsewhere.has(line.listingId)) {
        conflictListingIds.push(line.listingId);
        continue;
      }
      this.reservations.upsert({
        id: this.reservations.allocateId(),
        sessionId: session.id,
        listingId: line.listingId
      });
    }

    const updated = this.sessions.patch(session.id, { conflictListingIds })!;
    return mockResponse({ ...updated, conflictListingIds: [...conflictListingIds] });
  }

  getCheckoutSession(sessionId: number): Observable<CheckoutSessionState | null> {
    this.expireLapsedSessions();
    const session = this.sessions.get(sessionId);
    return mockResponse(session ? { ...session } : null);
  }

  releaseCheckout(sessionId: number): Observable<void> {
    this.endSession(sessionId, ReservationStatus.Released);
    return mockResponse(undefined);
  }

  placeOrder(request: PlaceOrderRequest): Observable<OrderState> {
    this.expireLapsedSessions();
    const session = this.sessions.get(request.sessionId);
    if (!session || session.status !== ReservationStatus.Active) {
      return mockError('Your reservation has expired - please restart checkout.');
    }

    const cart = this.carts.list().find(c => c.ownerKey === session.ownerKey);
    const lines = (cart?.lines ?? []).filter(l => !session.conflictListingIds.includes(l.listingId));
    if (lines.length === 0) {
      return mockError('There is nothing left to order - your items were sold to another shopper.');
    }

    const groups = this.buildGroups(lines, request);
    const order: OrderState = this.orders.upsert({
      id: this.orders.allocateId(),
      orderRef: nextOrderRef(this.orders.list()),
      placedAt: new Date(),
      email: request.contact.email,
      shopperId: request.shopperId,
      totals: buildOrderTotals(groups),
      groups
    });

    for (const line of lines) {
      this.listings.patch(line.listingId, { status: ListingStatus.Sold, soldCount: 1 });
    }
    this.endSession(session.id, ReservationStatus.Consumed);

    if (cart) {
      cart.lines = [];
      this.carts.upsert(cart);
    }

    return mockResponse({ ...order, groups: order.groups.map(g => ({ ...g, lines: [...g.lines] })) });
  }

  listOrders(shopperId: number, pageNumber: number, pageSize: number): Observable<PaginatedResult<OrderState>> {
    const all = this.orders
      .list()
      .filter(o => o.shopperId === shopperId)
      .sort((a, b) => b.placedAt.getTime() - a.placedAt.getTime());
    const totalCount = all.length;
    const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
    return mockResponse({
      totalCount,
      pageNumber,
      pageSize,
      totalPages,
      hasPreviousPage: pageNumber > 1,
      hasNextPage: pageNumber < totalPages,
      items: all.slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
    });
  }

  getOrder(orderRef: string): Observable<OrderState | null> {
    return mockResponse(this.orders.list().find(o => o.orderRef === orderRef) ?? null);
  }

  findByRefAndEmail(orderRef: string, email: string): Observable<OrderState | null> {
    const ref = orderRef.trim().toUpperCase();
    const normalizedEmail = email.trim().toLowerCase();
    const order = this.orders
      .list()
      .find(o => o.orderRef.toUpperCase() === ref && o.email.toLowerCase() === normalizedEmail);
    return mockResponse(order ?? null);
  }

  /** Group cart lines by (tenant, store), applying the chosen fulfilment. */
  private buildGroups(lines: CartState['lines'], request: PlaceOrderRequest): OrderGroupState[] {
    const byStore = new Map<number, CartState['lines']>();
    for (const line of lines) {
      const bucket = byStore.get(line.storeId) ?? [];
      bucket.push(line);
      byStore.set(line.storeId, bucket);
    }

    let groupId = 1;
    const groups: OrderGroupState[] = [];
    for (const [storeId, storeLines] of byStore) {
      const first = storeLines[0];
      const requested = request.groups.find(g => g.storeId === storeId);
      const fulfilment =
        requested?.fulfilment ??
        (storeLines.every(l => l.pickupAvailable) ? FulfilmentMethod.Pickup : FulfilmentMethod.Shipping);
      const store = this.stores.get(storeId);
      const isPickup = fulfilment === FulfilmentMethod.Pickup;
      // The shopper's chosen carrier service prices the group (SHP-01); the
      // line-level fallback covers orders placed before rates resolved.
      const itemsSubtotal = storeLines.reduce((sum, l) => sum + l.unitPrice, 0);
      const chosenRate = requested?.shippingRateCode
        ? this.rateByCode(storeId, itemsSubtotal, requested.shippingRateCode)
        : null;
      const shippingAmount = isPickup
        ? 0
        : (chosenRate?.amount ?? Math.max(...storeLines.map(l => l.shippingPrice ?? 0), 0));

      groups.push({
        id: groupId++,
        tenantId: first.tenantId,
        tenantName: first.tenantName,
        storeId,
        storeName: first.storeName,
        storeAddressLine: store?.addressLine ?? first.storeAddressLine,
        fulfilment,
        status: OrderGroupStatus.Paid,
        lines: storeLines.map((l, index) => ({
          id: index + 1,
          listingId: l.listingId,
          title: l.title,
          condition: l.condition,
          photoUrl: l.photoUrl,
          unitPrice: l.unitPrice
        })),
        itemsSubtotal: storeLines.reduce((sum, l) => sum + l.unitPrice, 0),
        shippingAmount,
        pickupTicket: isPickup ? mintPickupTicket(store) : null,
        trackingNumber: null,
        etaLabel: isPickup ? null : SHIPPING_ETA_FALLBACK,
        refund: null
      });
    }
    return groups;
  }

  private expireLapsedSessions(): void {
    const now = Date.now();
    for (const session of this.sessions.list()) {
      if (session.status === ReservationStatus.Active && session.reservationExpiresAt.getTime() <= now) {
        this.endSession(session.id, ReservationStatus.Expired);
      }
    }
  }

  private endSession(sessionId: number, status: ReservationStatus): void {
    this.sessions.patch(sessionId, { status });
    for (const reservation of this.reservations.list().filter(r => r.sessionId === sessionId)) {
      this.reservations.remove(reservation.id);
    }
  }
}
